import { motion } from "framer-motion";

const IMG = "https://images.pexels.com/photos/30844004/pexels-photo-30844004.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  { n: "01", title: "Dignity in Delivery", text: "Every litre we move and every order we fulfil is executed with dignity, honour and professional pride — it is the standard our name is built on." },
  { n: "02", title: "Uncompromising Safety", text: "Rigorous safety standards govern our fleet, our drivers and every product handling operation, without exception." },
  { n: "03", title: "Trusted Partnerships", text: "We proudly serve top oil marketers across the country as their dependable lifting and delivery partner, trip after trip." },
  { n: "04", title: "Reliability, Always", text: "On-time, every time — our word is our bond, from depot loading to final destination, anywhere in Nigeria." },
];

const EASE = [0.16, 1, 0.3, 1];

export default function About() {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#0B132B]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-4" data-testid="about-eyebrow">Who We Are</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-white leading-tight" data-testid="about-heading">
            A Name Synonymous with <span className="text-gradient-amber">Excellence</span> in Oil &amp; Gas
          </h2>
          <div className="mt-6 space-y-5 text-slate-300 text-base leading-relaxed" data-testid="about-copy">
            <p>
              Motaps Global Investment Limited stands tall as one of Nigeria&rsquo;s most reputable and fast-rising forces in the oil and gas industry. Renowned for diligence, integrity and an unwavering commitment to excellence, we deal in all petroleum products and have earned the confidence of the nation&rsquo;s leading oil marketers as their transporter of choice.
            </p>
            <p>
              From lifting products at major depots to delivering them safely to any destination across Nigeria, we handle every assignment with dignity, precision and honour. Our clients praise us not only for the reliability of our fleet, but for the character of our service — transparent, punctual and deeply professional.
            </p>
            <p>
              Beyond bulk haulage, we bring energy directly to the people through our door-to-door diesel supply service, powering homes, estates, factories, hospitals and businesses with premium-grade diesel delivered right to their doorstep. At Motaps Global, we do not just move petroleum products — we move with purpose, we serve with dignity, and we deliver with distinction.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="relative"
        >
          <div className="overflow-hidden rounded-2xl border border-slate-700/50">
            <img src={IMG} alt="Motaps Global tanker delivering petroleum products" className="w-full object-cover aspect-[4/3] grayscale-[40%] hover:grayscale-0 transition-all duration-700 hover:scale-105" data-testid="about-image" />
          </div>
          <div className="absolute -bottom-6 -left-4 md:-left-8 bg-[#1C2541] border border-slate-700/50 rounded-2xl p-5 backdrop-blur-md shadow-2xl" data-testid="about-badge">
            <p className="font-mono-stat text-3xl font-bold text-amber-500">100%</p>
            <p className="text-xs text-slate-400 mt-1">Commitment to dignified, dependable delivery</p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-24 md:mt-32" data-testid="about-values">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-amber-500 font-display font-semibold tracking-[0.35em] uppercase text-xs mb-10"
        >
          The Motaps Manifesto
        </motion.p>
        {CHAPTERS.map(({ n, title, text }, i) => (
          <motion.div
            key={n}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
            className="grid md:grid-cols-[100px_1fr_1.4fr] gap-4 md:gap-10 items-baseline py-8 md:py-10 border-t border-slate-800 last:border-b"
            data-testid={`chapter-${n}`}
          >
            <span className="font-mono-stat text-amber-600 text-lg md:text-xl font-bold">{n}</span>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white">{title}</h3>
            <p className="text-slate-400 leading-relaxed text-sm md:text-base">{text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
